import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * /admin/settings/pricing — the prime rate gate, READ-ONLY.
 *
 * 🔑 THERE IS NO ENTRY FORM (Hannah, 2026-11-03 — fix/prime-fetch). Prime comes only from the automatic check
 * (backend utils/primeFetch): two published prime series, accepted only when they equal the federal funds target's
 * upper bound + 3.00% from two more official sources. Staff see the last verified rate, when it was fetched and what
 * it was checked against, and why pricing is halted when it is. Nobody types a prime rate.
 */
export default function AdminSettingsPricing() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('doorly_admin_token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [state, setState]   = useState(null);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState('');

  const load = useCallback(async () => {
    setLoad(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/api/admin/prime-rate`, { headers });
      setState(data);
    } catch (e) { setErr(e.response?.data?.error || 'Could not read the prime rate state.'); }
    finally { setLoad(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const KIND = {
    ok:                 { bg: '#f0fdf4', bd: '#acf44e', fg: '#166534', label: '✓ Pricing is open' },
    stale:              { bg: '#fff7ed', bd: '#fed7aa', fg: '#7a5200', label: '🛑 Pricing halted — the held rate predates an FOMC decision' },
    invalid:            { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', label: '🛑 Pricing halted — the held rate is not a possible prime rate' },
    calendar_exhausted: { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', label: '🛑 Pricing halted — the FOMC calendar has run out' },
    empty:              { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', label: '🛑 Pricing halted — no prime rate on record' },
  };
  const k = KIND[state?.kind] || KIND.empty;

  return (
    <AdminLayout>
      <div style={{ padding: '24px 28px', maxWidth: 820 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Pricing — Prime Rate</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6 }}>
          Every priced file is built on this number: the base rate is prime + 2.00%. Prime is checked
          automatically every day and is accepted only when the published prime rate equals the top of
          the Federal Reserve's target range plus 3.00%. Nobody enters it by hand. If an FOMC decision
          has taken effect and today's prime can't be verified, pricing stops until it is.
        </p>

        {loading && <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div>}
        {err && <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#9f1239' }}>{err}</div>}

        {state && (
          <>
            <div style={{ background: k.bg, border: `2px solid ${k.bd}`, borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: k.fg, marginBottom: 8 }}>{k.label}</div>
              {state.held ? (
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                  <div><strong>Verified prime:</strong> {state.held.pct}</div>
                  <div><strong>Fetched:</strong> {new Date(state.held.fetchedAt).toLocaleString()}</div>
                  <div><strong>Source:</strong> {state.held.source || '—'}</div>
                  <div><strong>Checked against:</strong> {state.held.verifiedAgainst || '—'}</div>
                  {state.staleSince && <div style={{ marginTop: 6 }}><strong>Decision since:</strong> {state.staleSince}</div>}
                  {state.invalidReason && <div style={{ marginTop: 6, color: '#9f1239' }}>{state.invalidReason}</div>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#374151' }}>No rate on record.</div>
              )}
              {state.failingSince && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#7a5200', lineHeight: 1.6 }}>The automatic check has been failing since {new Date(state.failingSince).toLocaleString()}.</div>
              )}
              {state.envIgnored && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#9f1239', lineHeight: 1.6 }}>PRIME_RATE is set in Render and is ignored. Delete it in Render (doorly-backend → Environment).</div>
              )}
              {!state.calendar?.ok && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#9f1239', lineHeight: 1.6 }}>{state.calendar.reason}</div>
              )}
            </div>

          </>
        )}

      </div>
    </AdminLayout>
  );
}
